import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Stack,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Drawer,
  Divider,
  Chip,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Tab,
  Tabs,
  TextField,
  Button, // <-- ajoutés ici
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
// Icons
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import EventNoteIcon from '@mui/icons-material/EventNote';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import LoginIcon from '@mui/icons-material/Login';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';

import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import AppPagination from '../../components/base/AppPagination';
import { usePagination } from '../../hooks/usePagination';
import ConfirmDialog from '../../components/base/ConfirmDialog';
import AppSearch from '../../components/base/AppSearch';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
// --- Sous-composants utilitaires ---

const DetailItem = ({ label, value, icon, color, labelColor }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 1.2,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      {icon &&
        React.cloneElement(icon, {
          sx: { color: '#A3AED0', fontSize: '18px' },
        })}
      <Typography
        sx={{
          color: labelColor || '#A3AED0',
          fontSize: '14px',
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
    </Stack>
    <Typography
      sx={{
        color: color || '#2B3674',
        fontSize: '14px',
        fontWeight: 700,
        textAlign: 'right',
        ml: 2,
      }}
    >
      {value || '—'}
    </Typography>
  </Box>
);

const StatCard = ({ label, value, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      bgcolor: 'white',
      borderRadius: '16px',
      textAlign: 'center',
      flex: 1,
      border: '1px solid #F4F7FE',
    }}
  >
    <Typography variant="caption" sx={{ color: '#A3AED0', fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography variant="h6" sx={{ color: color, fontWeight: 800 }}>
      {value || 0}
    </Typography>
  </Paper>
);

// --- Composant Principal ---

const Clients = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  // ... vos autres states (openCreateDialog, etc.)
  const [, setAddFile] = useState(null); // Pour stocker le fichier réel
  const [addPreview, setAddPreview] = useState(null); // Pour l'affichage de l'image (URL)
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');

  const rolePath = useMemo(() => {
    // On vérifie si role_user est 1 (Super Admin) ou 2 (Admin)
    const isAdmin = user?.role === 1 || user?.role === 2;

    return isAdmin ? 'admin' : 'intervenant';
  }, [user]);
  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}/${rolePath}/clients/list/all`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setClients(response.data.result || []);
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token, rolePath]);

  useEffect(() => {
    if (user) fetchClients();
  }, [user, fetchClients]);

  const filteredClients = useMemo(() => {
    return clients.filter((c) => {
      const searchStr = searchTerm.toLowerCase();
      return (
        c.nom_client?.toLowerCase().includes(searchStr) ||
        c.prenom_client?.toLowerCase().includes(searchStr) ||
        c.email_client?.toLowerCase().includes(searchStr) ||
        c.phone_client?.includes(searchTerm)
      );
    });
  }, [clients, searchTerm]);

  const itemsPerPage = 8;
  const { next, currentData, currentPage, count, totalItems } = usePagination(
    filteredClients,
    itemsPerPage
  );

  const handleConfirmDelete = async () => {
    // 1. On vérifie si clientToDelete existe bien pour éviter les erreurs
    if (!clientToDelete) return;

    try {
      // 3. Appel API avec l'URL dynamique
      await axios.delete(
        `${apiUrl}/${rolePath}/clients/delete/${clientToDelete.id_client}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success('Client supprimé avec succès');

      // 4. Rafraîchissement des données
      fetchClients();

      // Si le client supprimé était celui affiché dans les détails, on ferme la vue
      if (selectedClient?.id_client === clientToDelete.id_client) {
        setSelectedClient(null);
      }
    } catch (err) {
      // On récupère le message d'erreur du backend (ex: "Accès refusé")
      const errorMsg =
        err.response?.data?.message || 'Erreur lors de la suppression';
      toast.error(errorMsg);
      console.error('Delete Error:', err.response?.data);
    } finally {
      // 5. Nettoyage des états du Dialog dans tous les cas
      setDeleteDialogOpen(false);
      setClientToDelete(null);
    }
  };

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [reservationPage, setReservationPage] = useState(1);
  const reservationsPerPage = 9;

  const paginatedReservations = useMemo(() => {
    if (!selectedClient?.reservations) return [];
    const startIndex = (reservationPage - 1) * reservationsPerPage;
    return selectedClient.reservations.slice(
      startIndex,
      startIndex + reservationsPerPage
    );
  }, [selectedClient, reservationPage]);

  const totalReservationPages = Math.ceil(
    (selectedClient?.reservations?.length || 0) / reservationsPerPage
  );
  const handleViewOrderDetails = (cmd) => {
    setSelectedOrder(cmd);
    setOrderDetailsOpen(true);
  };

  const [subscriptionPage, setSubscriptionPage] = useState(1);
  const subscriptionsPerPage = 5;
  const paginatedSubscriptions = useMemo(() => {
    if (!selectedClient?.subscriptions) return [];
    const startIndex = (subscriptionPage - 1) * subscriptionsPerPage;
    return selectedClient.subscriptions.slice(
      startIndex,
      startIndex + subscriptionsPerPage
    );
  }, [selectedClient, subscriptionPage]);

  const totalSubscriptionPages = Math.ceil(
    (selectedClient?.subscriptions?.length || 0) / subscriptionsPerPage
  );
  useEffect(() => {
    setSubscriptionPage(1);
  }, [selectedClient, tabValue]);
  const thStyle = {
    color: '#A3AED0',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  };

  const tdStyle = {
    color: 'white',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  };

  // update client
  const [editClient, setEditClient] = useState(null);
  const [, setIsEditing] = useState(false);
  useEffect(() => {
    if (selectedClient) {
      setEditClient(selectedClient);
    }
  }, [selectedClient]);

  const handleUpdateClient = async () => {
    try {
      // 1. On détermine le préfixe de l'URL selon le rôle de l'utilisateur connecté

      // 2. On utilise axios.patch au lieu de put (pour correspondre à la route Laravel)
      await axios.patch(
        `${apiUrl}/${rolePath}/clients/update/${editClient.id_client}`,
        editClient,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success('Client mis à jour avec succès');

      // Si tu utilises un modal ou un affichage conditionnel
      if (typeof setIsEditing === 'function') setIsEditing(false);

      fetchClients(); // Rafraîchir la liste
    } catch (err) {
      // Affiche l'erreur précise du validateur si disponible
      const errorMsg =
        err.response?.data?.message || 'Erreur lors de la mise à jour';
      toast.error(errorMsg);
      console.error('Update Error:', err.response?.data);
    }
  };

  const usePaginatedData = (data, itemsPerPage) => {
    const [page, setPage] = useState(1);

    const currentData = useMemo(() => {
      const start = (page - 1) * itemsPerPage;
      return data.slice(start, start + itemsPerPage);
    }, [data, page, itemsPerPage]);

    const totalPages = Math.ceil(data.length / itemsPerPage);

    return { currentData, page, setPage, totalPages };
  };
  const {
    currentData: paginatedCommands,
    page: commandPage,
    setPage: setCommandPage,
    totalPages: totalCommandPages,
  } = usePaginatedData(selectedClient?.commands || [], 9);

  // À placer avant le bloc "return (" de ton composant
  const getFieldProps = (field) => ({
    label: field.label,
    value: editClient?.[field.key] || '',
    onChange: (e) =>
      setEditClient({ ...editClient, [field.key]: e.target.value }),
    fullWidth: true,
    variant: 'filled',
    InputProps: { disableUnderline: true },
    sx: {
      '& .MuiFilledInput-root': {
        bgcolor: '#111C44',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        height: '56px',
        color: 'white',
        '&:hover': {
          bgcolor: '#111C44',
          borderColor: 'rgba(255, 255, 255, 0.2)',
        },
        '&.Mui-focused': {
          bgcolor: '#111C44',
          borderColor: '#4318FF',
        },
      },
      '& .MuiInputLabel-root': {
        color: '#A3AED0',
        '&.Mui-focused': { color: '#4318FF' },
      },
    },
  });

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newClient, setNewClient] = useState({
    nom_client: '',
    prenom_client: '',
    email_client: '',
    password_client: '', // Requis par ton Validator
    addresse_client: '',
    ville_client: '',
    cp_client: '',
    phone_client: '',
    province_client: '', // Optionnel dans ton validator mais utilisé dans ton model
  });
  const handleCreateClient = async () => {
    try {
      // 1. Utilise la route correcte (vérifie si c'est /inscription ou /create dans ton backend)
      await axios.post(`${apiUrl}/${rolePath}/clients/inscription`, newClient, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Client créé avec succès');
      setOpenCreateDialog(false);

      // 2. Reset COMPLET de l'objet pour éviter les données résiduelles
      setNewClient({
        nom_client: '',
        prenom_client: '',
        email_client: '',
        password_client: '',
        addresse_client: '',
        ville_client: '',
        cp_client: '',
        phone_client: '',
        province_client: '',
      });

      fetchClients();
    } catch (err) {
      console.error('Erreur Backend:', err.response?.data);
      toast.error(err.response?.data?.message || 'Erreur lors de la création');
    }
  };
  const getFieldPropsForCreate = (field, label, type = 'text') => ({
    label: label,
    type: type,
    fullWidth: true,
    variant: 'filled',
    value: newClient[field],
    onChange: (e) => setNewClient({ ...newClient, [field]: e.target.value }),
    InputProps: { disableUnderline: true },
    sx: {
      '& .MuiOutlinedInput-root': {
        // Utilise Outlined pour plus de finesse
        bgcolor: '#ffffff',
        borderRadius: '10px',
        color: '#4318FF', // Texte bleu nuit très pro
        transition: 'all 0.2s ease-in-out',
        '& fieldset': {
          borderColor: '#E0E5F2', // Bordure gris clair
        },
        '&:hover fieldset': {
          borderColor: '#B0BBD5', // Bordure un peu plus sombre au survol
        },
        '&.Mui-focused fieldset': {
          borderColor: '#000000', // Couleur d'accentuation (Bleu Horizon)
          borderWidth: '2px',
        },
      },
      '& .MuiInputLabel-root': {
        color: '#A3AED0',
        fontSize: '0.875rem',
        '&.Mui-focused': { color: '#000000' },
      },
    },
  });

  const handleExportClients = async () => {
    try {
      toast.info("Préparation de l'export...");

      const response = await axios.get(
        `${apiUrl}/${rolePath}/clients/exports/all`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `export_clients_${date}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Exportation terminée !');
    } catch (err) {
      // --- BLOC DE DÉBOGAGE POUR LES BLOBS ---
      if (err.response && err.response.data instanceof Blob) {
        const reader = new FileReader();

        reader.onload = () => {
          try {
            // On transforme le texte lu en objet JSON
            const errorJson = JSON.parse(reader.result);
            console.error('ERREUR API DÉCODÉE :', errorJson);
            toast.error(
              `Erreur API : ${errorJson.message || 'Problème serveur'}`
            );
          } catch (e) {
            // Si ce n'est pas du JSON, c'est peut-être une erreur PHP brute (HTML)
            console.error('ERREUR SERVEUR (HTML/TEXTE) :', reader.result);
            toast.error('Le serveur a renvoyé une erreur critique.');
          }
        };

        reader.readAsText(err.response.data);
      } else {
        console.error('Export error:', err);
        toast.error('Erreur lors de la connexion au serveur');
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header & Recherche */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 6 }}
        spacing={2}
      >
        <Typography
          variant="h3"
          sx={{
            fontWeight: '800',
            color: '#2B3674',
            fontSize: { xs: '1.8rem', md: '2.5rem' },
          }}
        >
          Gestion Clients
        </Typography>

        <Stack direction="row" spacing={2}>
          <AppSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Rechercher un client..."
          />
          {/* BOUTON EXPORT */}
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />} // N'oublie pas d'importer l'icône
            onClick={handleExportClients}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              px: 8, // Réduit le padding (12 était trop large)

              borderColor: '#E0E5F2',
              color: '#2B3674',
              '&:hover': {
                borderColor: '#B0BBD5',
                bgcolor: '#F4F7FE',
              },
            }}
          >
            Exporter
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateDialog(true)}
            sx={{
              bgcolor: '#4318FF',
              borderRadius: '20px', // Plus arrondi pour l'effet "pilule"
              px: 8, // Réduit le padding (12 était trop large)
              py: 1.5, // Ajuste le padding vertical au lieu d'une hauteur fixe
              textTransform: 'none',
              fontSize: '14px', // Taille de police standard dashboard
              fontWeight: 600, // Pour plus de lisibilité
              whiteSpace: 'nowrap', // Force le texte sur une seule ligne
              boxShadow: '0px 4px 12px rgba(67, 24, 255, 0.2)', // Optionnel: petite ombre portée
              '&:hover': {
                bgcolor: '#3311CC',
                boxShadow: 'none',
              },
            }}
          >
            Ajouter un client
          </Button>
        </Stack>
      </Stack>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '20px',
          overflow: 'hidden',
          border: '1px solid #F4F7FE',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#F4F7FE' }}>
              <TableRow>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  CLIENT
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  TÉLÉPHONE
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  VÉRIFIÉ
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  STATUT
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  DATE INSCRIPTION
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: '#A3AED0', fontWeight: 700 }}
                >
                  ACTIONS
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress
                      size={30}
                      sx={{ my: 4, color: '#4318FF' }}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                currentData().map((client) => (
                  <TableRow key={client.id_client} hover>
                    <TableCell>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: '#4318FF', fontWeight: 700 }}>
                          {client.prenom_client?.[0]}
                          {client.nom_client?.[0]}
                        </Avatar>
                        <Box>
                          <Typography
                            sx={{ color: '#2B3674', fontWeight: 700 }}
                          >
                            {client.prenom_client} {client.nom_client}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: '#A3AED0' }}
                          >
                            {client.email_client}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ color: '#2B3674', fontWeight: 600 }}>
                      {client.phone_client || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={
                          client.email_verified === 1 ? (
                            <CheckCircleOutlineIcon
                              style={{ color: '#05CD99' }}
                            />
                          ) : (
                            <HighlightOffIcon style={{ color: '#EE5D50' }} />
                          )
                        }
                        label={client.email_verified === 1 ? 'Oui' : 'Non'}
                        variant="outlined"
                        size="small"
                        sx={{
                          borderColor:
                            client.email_verified === 1 ? '#05CD99' : '#EE5D50',
                          color:
                            client.email_verified === 1 ? '#05CD99' : '#EE5D50',
                          fontWeight: 700,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          client.etat_client === 'active' ? 'Actif' : 'Inactif'
                        }
                        size="small"
                        sx={{
                          bgcolor:
                            client.etat_client === 'active'
                              ? '#E6F9F0'
                              : '#FFF5F5',
                          color:
                            client.etat_client === 'active'
                              ? '#05CD99'
                              : '#EE5D50',
                          fontWeight: 700,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#2B3674', fontWeight: 600 }}>
                      {client.date_inscrip_client
                        ? new Date(
                            client.date_inscrip_client
                          ).toLocaleDateString('fr-FR')
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Quick View">
                        <IconButton
                          onClick={() => {
                            setSelectedClient(client);
                            setOpenDrawer(true);
                          }}
                          sx={{
                            color: '#4318FF',
                            bgcolor: '#F4F7FE',
                            mr: 1,
                            '&:hover': { bgcolor: '#E9EDF7' },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Plus de détails">
                        <IconButton
                          onClick={() => {
                            setSelectedClient(client);
                            setOpenPopup(true);
                          }}
                          sx={{ color: '#2B3674', bgcolor: '#E9EDF7', mr: 1 }}
                        >
                          <AssignmentIndIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* Bouton de suppression visible pour tous */}
                      <Tooltip title="Supprimer le client">
                        <IconButton
                          color="error"
                          onClick={() => {
                            setClientToDelete(client);
                            setDeleteDialogOpen(true);
                          }}
                          sx={{
                            bgcolor: '#FFF5F5',
                            '&:hover': { bgcolor: '#FFE0E0' },
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {user?.role === 'admin' && (
                        <IconButton
                          color="error"
                          onClick={() => {
                            setClientToDelete(client);
                            setDeleteDialogOpen(true);
                          }}
                          sx={{ bgcolor: '#FFF5F5' }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <AppPagination
          count={count}
          page={currentPage}
          onChange={next}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
        />
      </Paper>

      {/* --- DRAWER : VOTRE QUICK VIEW D'ORIGINE --- */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        sx={{ zIndex: 3000 }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 500 }, p: 3 } }}
      >
        {selectedClient && (
          <Box sx={{ p: 3 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={4}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, color: '#2B3674' }}
              >
                Fiche Client
              </Typography>
              <IconButton onClick={() => setOpenDrawer(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>

            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  mb: 2,
                  bgcolor: '#4318FF',
                  fontSize: '2rem',
                }}
              >
                {selectedClient.prenom_client?.[0]}
                {selectedClient.nom_client?.[0]}
              </Avatar>
              <Typography
                variant="h5"
                sx={{ fontWeight: 800, color: '#2B3674' }}
              >
                {selectedClient.prenom_client} {selectedClient.nom_client}
              </Typography>
              <Chip
                label={`ID: #${selectedClient.id_client}`}
                size="small"
                sx={{ mt: 1 }}
              />
            </Box>

            <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
              <StatCard
                label="Vidéos"
                value={selectedClient.nbr_video}
                color="#4318FF"
              />
              <StatCard
                label="Workshops"
                value={selectedClient.nbr_workshops}
                color="#05CD99"
              />
              <StatCard
                label="Offres"
                value={selectedClient.nbr_offre}
                color="#FFB547"
              />
            </Stack>

            <Typography sx={{ color: '#2B3674', fontWeight: 800, mb: 2 }}>
              Informations de contact
            </Typography>
            <Paper
              elevation={0}
              sx={{ p: 2, bgcolor: '#F4F7FE', borderRadius: '15px', mb: 4 }}
            >
              <DetailItem
                icon={<EmailIcon />}
                label="Email"
                value={selectedClient.email_client}
              />
              <DetailItem
                icon={<LocalPhoneIcon />}
                label="Téléphone"
                value={selectedClient.phone_client}
              />
              <DetailItem
                icon={<LocationOnIcon />}
                label="Adresse"
                value={`${selectedClient.addresse_client || ''}, ${selectedClient.cp_client || ''} ${selectedClient.ville_client || ''}`}
              />
              <Divider sx={{ my: 1.5, opacity: 0.5 }} />
              <DetailItem
                icon={<LoginIcon />}
                label="Dernière connexion"
                value={
                  selectedClient.last_cnx_client
                    ? new Date(selectedClient.last_cnx_client).toLocaleString(
                        'fr-FR'
                      )
                    : 'Jamais connecté'
                }
                color="#4318FF"
              />
              <DetailItem
                icon={<EventNoteIcon />}
                label="Date d'inscription"
                value={
                  selectedClient.date_inscrip_client
                    ? new Date(
                        selectedClient.date_inscrip_client
                      ).toLocaleDateString('fr-FR')
                    : '—'
                }
              />
              <DetailItem
                icon={<CheckCircleOutlineIcon />}
                label="Compte vérifié"
                value={selectedClient.email_verified === 1 ? 'Oui' : 'Non'}
                color={
                  selectedClient.email_verified === 1 ? '#05CD99' : '#EE5D50'
                }
              />
            </Paper>

            <Typography sx={{ color: '#2B3674', fontWeight: 800, mb: 2 }}>
              Récapitulatif Activité
            </Typography>
            <Stack spacing={1}>
              <DetailItem
                label="Nombre de commandes"
                value={selectedClient.commands?.length || 0}
              />
              <DetailItem
                label="Nombre de réservations"
                value={selectedClient.reservations?.length || 0}
              />
              <DetailItem
                label="Abonnements actifs"
                value={selectedClient.subscriptions?.length || 0}
              />
            </Stack>
          </Box>
        )}
      </Drawer>

      {/* --- POP-UP : LE NOUVEAU DESIGN COMPLET --- */}
      <Dialog
        open={openPopup}
        onClose={() => setOpenPopup(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            bgcolor: '#111C44',
            color: 'white',
            minHeight: '80vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Profil du client
          </Typography>
          <IconButton
            onClick={() => setOpenPopup(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Colonne Gauche */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  bgcolor: '#1B254B',
                  p: 3,
                  borderRadius: '20px',
                  textAlign: 'center',
                  mb: 3,
                }}
              >
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    mx: 'auto',
                    mb: 2,
                    border: '4px solid #111C44',
                    bgcolor: '#4318FF',
                  }}
                >
                  {selectedClient?.prenom_client?.[0]}
                  {selectedClient?.nom_client?.[0]}
                </Avatar>
                <Typography variant="h5" fontWeight={700}>
                  {selectedClient?.prenom_client} {selectedClient?.nom_client}
                </Typography>
                <Typography variant="body2" sx={{ color: '#A3AED0', mb: 2 }}>
                  {selectedClient?.email_client}
                </Typography>
                <Chip
                  label={
                    selectedClient?.etat_client === 'active'
                      ? 'actif'
                      : 'inactif'
                  }
                  size="small"
                  sx={{
                    bgcolor: '#05CD99',
                    color: 'white',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                  }}
                />

                <Box sx={{ mt: 4, textAlign: 'left' }}>
                  <DetailItem
                    label="Status"
                    value={selectedClient?.etat_client}
                    color="#05CD99"
                    labelColor="#A3AED0"
                  />
                  <Divider
                    sx={{ my: 1, borderColor: 'rgba(255,255,255,0.05)' }}
                  />
                  <DetailItem
                    label="Connexion"
                    value={
                      selectedClient?.last_cnx_client
                        ? new Date(
                            selectedClient.last_cnx_client
                          ).toLocaleDateString()
                        : '—'
                    }
                    labelColor="#A3AED0"
                    color="white"
                  />
                  <Divider
                    sx={{ my: 1, borderColor: 'rgba(255,255,255,0.05)' }}
                  />
                  <DetailItem
                    label="Inscription"
                    value={selectedClient?.date_inscrip_client}
                    color="white"
                    labelColor="#A3AED0"
                  />
                </Box>
              </Box>

              <Box sx={{ bgcolor: '#1B254B', p: 3, borderRadius: '20px' }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                  À propos
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#A3AED0' }}>
                      Adresse client
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      {selectedClient?.addresse_client || 'Non renseignée'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#A3AED0' }}>
                      Ville
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      {selectedClient?.ville_client || 'Non renseignée'}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Grid>

            {/* Colonne Droite */}
            <Grid item xs={12} md={8}>
              <Box
                sx={{ bgcolor: '#1B254B', borderRadius: '20px', p: 1, mb: 3 }}
              >
                <Tabs
                  value={tabValue}
                  onChange={(e, v) => setTabValue(v)}
                  sx={{
                    '& .MuiTabs-indicator': { display: 'none' },
                    '& .MuiTab-root': {
                      color: '#A3AED0',
                      borderRadius: '12px',
                      minHeight: '40px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '12px',
                    },
                    '& .Mui-selected': {
                      bgcolor: '#4318FF',
                      color: 'white !important',
                    },
                  }}
                >
                  <Tab label="Commandes" />
                  <Tab label="Abonnements" />
                  <Tab label="Reservation" />
                  <Tab label="Settings" />
                </Tabs>
              </Box>

              <Box
                sx={{
                  bgcolor: '#1B254B',
                  p: 3,
                  borderRadius: '20px',
                  minHeight: '400px',
                }}
              >
                {tabValue === 0 && (
                  <>
                    <Typography variant="h6" fontWeight={700} mb={3}>
                      Liste des commandes
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                color: '#A3AED0',
                                borderBottom: '1px solid rgba(255,255,255,0.1)',
                              }}
                            >
                              ID
                            </TableCell>
                            <TableCell
                              sx={{
                                color: '#A3AED0',
                                borderBottom: '1px solid rgba(255,255,255,0.1)',
                              }}
                            >
                              Date
                            </TableCell>
                            <TableCell
                              sx={{
                                color: '#A3AED0',
                                borderBottom: '1px solid rgba(255,255,255,0.1)',
                              }}
                            >
                              Référence
                            </TableCell>
                            <TableCell
                              sx={{
                                color: '#A3AED0',
                                borderBottom: '1px solid rgba(255,255,255,0.1)',
                              }}
                            >
                              Type
                            </TableCell>
                            <TableCell
                              sx={{
                                color: '#A3AED0',
                                borderBottom: '1px solid rgba(255,255,255,0.1)',
                              }}
                            >
                              Prix total
                            </TableCell>
                            <TableCell
                              sx={{
                                color: '#A3AED0',
                                borderBottom: '1px solid rgba(255,255,255,0.1)',
                              }}
                            >
                              État
                            </TableCell>
                            <TableCell
                              sx={{
                                color: '#A3AED0',
                                borderBottom: '1px solid rgba(255,255,255,0.1)',
                              }}
                              align="right"
                            >
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedCommands.length > 0 ? (
                            paginatedCommands.map((cmd, index) => (
                              <TableRow key={cmd.id_cmd || index}>
                                {/* ID */}
                                <TableCell
                                  sx={{
                                    color: 'white',
                                    borderBottom:
                                      '1px solid rgba(255,255,255,0.05)',
                                  }}
                                >
                                  #{cmd.id_cmd}
                                </TableCell>

                                {/* DATE DE CRÉATION */}
                                <TableCell
                                  sx={{
                                    color: 'rgba(255,255,255,0.7)',
                                    borderBottom:
                                      '1px solid rgba(255,255,255,0.05)',
                                    fontSize: '0.85rem',
                                  }}
                                >
                                  {cmd.created_at
                                    ? new Date(
                                        cmd.created_at
                                      ).toLocaleDateString('fr-FR')
                                    : '—'}
                                </TableCell>

                                {/* RÉFÉRENCE */}
                                <TableCell
                                  sx={{
                                    color: 'white',
                                    borderBottom:
                                      '1px solid rgba(255,255,255,0.05)',
                                    fontWeight: 500,
                                  }}
                                >
                                  {cmd.ref_cmd || '—'}
                                </TableCell>

                                {/* TYPE */}
                                <TableCell
                                  sx={{
                                    color: 'rgba(255,255,255,0.6)',
                                    borderBottom:
                                      '1px solid rgba(255,255,255,0.05)',
                                    fontSize: '0.85rem',
                                  }}
                                >
                                  {cmd.type_cmd?.replace('_', ' ')}
                                </TableCell>

                                {/* PRIX TOTAL */}
                                <TableCell
                                  sx={{
                                    color: 'white',
                                    borderBottom:
                                      '1px solid rgba(255,255,255,0.05)',
                                    fontWeight: 700,
                                  }}
                                >
                                  {cmd.total_ttc_cmd} €
                                </TableCell>

                                {/* ÉTAT (Statut) */}
                                <TableCell
                                  sx={{
                                    borderBottom:
                                      '1px solid rgba(255,255,255,0.05)',
                                  }}
                                >
                                  <Chip
                                    label={cmd.etat_cmd}
                                    size="small"
                                    sx={{
                                      bgcolor:
                                        cmd.etat_cmd === 'SUCCESS'
                                          ? 'rgba(5, 205, 153, 0.1)'
                                          : 'rgba(255, 184, 0, 0.1)',
                                      color:
                                        cmd.etat_cmd === 'SUCCESS'
                                          ? '#05CD99'
                                          : '#FFB800',
                                      fontWeight: 700,
                                      fontSize: '10px',
                                      textTransform: 'uppercase',
                                    }}
                                  />
                                </TableCell>

                                {/* BOUTON DÉTAILS */}
                                <TableCell
                                  sx={{
                                    borderBottom:
                                      '1px solid rgba(255,255,255,0.05)',
                                  }}
                                  align="right"
                                >
                                  <Tooltip title="Voir les détails de la commande">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleViewOrderDetails(cmd)
                                      } // Remplace par ta fonction de gestion
                                      sx={{
                                        color: '#4481EB',
                                        bgcolor: 'rgba(68, 129, 235, 0.1)',
                                        '&:hover': {
                                          bgcolor: 'rgba(68, 129, 235, 0.2)',
                                        },
                                      }}
                                    >
                                      <VisibilityIcon
                                        sx={{ fontSize: '18px' }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={7}
                                align="center"
                                sx={{ py: 8, border: 'none' }}
                              >
                                <Typography
                                  sx={{
                                    color: 'rgba(255,255,255,0.3)',
                                    fontStyle: 'italic',
                                  }}
                                >
                                  Aucune commande enregistrée pour ce client.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {/* PAGINATION DES COMMANDES */}
                    {totalCommandPages > 1 && (
                      <Stack
                        spacing={2}
                        alignItems="center"
                        sx={{ mt: 3, mb: 1 }}
                      >
                        <AppPagination
                          count={totalCommandPages}
                          page={commandPage}
                          onChange={(e, p) => setCommandPage(p)}
                          color="primary"
                          size="small"
                        />
                      </Stack>
                    )}

                    <Dialog
                      open={orderDetailsOpen}
                      onClose={() => setOrderDetailsOpen(false)}
                      maxWidth="md"
                      fullWidth
                      PaperProps={{
                        sx: {
                          bgcolor: '#111C44',
                          color: 'white',
                          borderRadius: '24px',
                          backgroundImage: 'none',
                          overflowX: 'hidden', // Sécurité anti-scroll
                        },
                      }}
                    >
                      <DialogTitle
                        sx={{
                          p: 3,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          Détails Commande{' '}
                          <span style={{ color: '#4481EB', marginLeft: '8px' }}>
                            {selectedOrder?.ref_cmd}
                          </span>
                        </Typography>
                        <IconButton
                          onClick={() => setOrderDetailsOpen(false)}
                          sx={{ color: 'rgba(255,255,255,0.5)' }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </DialogTitle>

                      <DialogContent
                        sx={{
                          p: 3,
                          pt: 0,
                          overflowX: 'hidden', // Empêche le scroll ici aussi
                        }}
                      >
                        {/* Utilisation de Flexbox simple au lieu de Grid pour éviter les marges négatives */}
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 3,
                            flexDirection: { xs: 'column', md: 'row' }, // Stack sur mobile, côte à côte sur PC
                            mb: 4,
                            width: '100%',
                          }}
                        >
                          {/* BLOC GAUCHE */}
                          <Box
                            sx={{
                              flex: 1,
                              bgcolor: 'rgba(255,255,255,0.02)',
                              p: 3,
                              borderRadius: '16px',
                              border: '1px solid rgba(255,255,255,0.05)',
                            }}
                          >
                            <Typography
                              variant="overline"
                              sx={{
                                color: '#4481EB',
                                fontWeight: 800,
                                mb: 2,
                                display: 'block',
                              }}
                            >
                              Informations Générales
                            </Typography>
                            <Stack spacing={2}>
                              {[
                                {
                                  label: 'ID Commande',
                                  value: `#${selectedOrder?.id_cmd}`,
                                },
                                {
                                  label: 'Type',
                                  value: selectedOrder?.type_cmd?.replace(
                                    '_',
                                    ' '
                                  ),
                                },
                                {
                                  label: 'Date de création',
                                  value:
                                    selectedOrder?.created_at &&
                                    new Date(
                                      selectedOrder.created_at
                                    ).toLocaleString('fr-FR'),
                                },
                                {
                                  label: 'Mise à jour',
                                  value:
                                    selectedOrder?.updated_at &&
                                    new Date(
                                      selectedOrder.updated_at
                                    ).toLocaleString('fr-FR'),
                                },
                              ].map((item, i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    borderBottom:
                                      '1px solid rgba(255,255,255,0.05)',
                                    pb: 1,
                                  }}
                                >
                                  <Typography
                                    sx={{ color: '#A3AED0', fontSize: '14px' }}
                                  >
                                    {item.label}
                                  </Typography>
                                  <Typography sx={{ fontWeight: 600 }}>
                                    {item.value}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          </Box>

                          {/* BLOC DROITE */}
                          <Box
                            sx={{
                              width: { xs: '100%', md: '350px' },
                              bgcolor: 'rgba(68, 129, 235, 0.05)',
                              p: 3,
                              borderRadius: '16px',
                              border: '1px solid rgba(68, 129, 235, 0.2)',
                            }}
                          >
                            <Typography
                              variant="overline"
                              sx={{
                                color: '#4481EB',
                                fontWeight: 800,
                                mb: 2,
                                display: 'block',
                              }}
                            >
                              Résumé Financier
                            </Typography>
                            <Stack spacing={1.5}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <Typography sx={{ color: '#A3AED0' }}>
                                  Total HT
                                </Typography>
                                <Typography>
                                  {selectedOrder?.total_htc_cmd} €
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <Typography sx={{ color: '#A3AED0' }}>
                                  TVA
                                </Typography>
                                <Typography>
                                  {selectedOrder?.total_tva_cmd} €
                                </Typography>
                              </Box>
                              <Divider
                                sx={{
                                  my: 1,
                                  borderColor: 'rgba(255,255,255,0.1)',
                                }}
                              />
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <Typography
                                  sx={{ color: '#05CD99', fontWeight: 700 }}
                                >
                                  Total TTC
                                </Typography>
                                <Typography
                                  variant="h5"
                                  sx={{ color: '#05CD99', fontWeight: 800 }}
                                >
                                  {selectedOrder?.total_ttc_cmd} €
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  mt: 2,
                                  p: 2,
                                  borderRadius: '12px',
                                  bgcolor: 'rgba(5, 205, 153, 0.1)',
                                }}
                              >
                                <Typography
                                  sx={{ color: '#05CD99', fontWeight: 600 }}
                                >
                                  Montant Payé
                                </Typography>
                                <Typography
                                  sx={{ color: '#05CD99', fontWeight: 800 }}
                                >
                                  {selectedOrder?.amount_paid} €
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        </Box>

                        {/* ARTICLES TABLEAU */}
                        <Typography
                          variant="overline"
                          sx={{
                            color: '#A3AED0',
                            fontWeight: 800,
                            mb: 1,
                            display: 'block',
                          }}
                        >
                          Articles commandés
                        </Typography>
                        <TableContainer
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.02)',
                            borderRadius: '16px',
                            border: '1px solid rgba(255,255,255,0.05)',
                            width: '100%',
                            overflowX: 'auto', // Autorise le scroll uniquement dans le tableau si besoin
                          }}
                        >
                          <Table>
                            <TableHead
                              sx={{ bgcolor: 'rgba(255,255,255,0.03)' }}
                            >
                              <TableRow>
                                <TableCell
                                  sx={{
                                    color: '#A3AED0',
                                    fontWeight: 700,
                                    border: 'none',
                                  }}
                                >
                                  Offre
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: '#A3AED0',
                                    fontWeight: 700,
                                    border: 'none',
                                  }}
                                >
                                  Description
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: '#A3AED0',
                                    fontWeight: 700,
                                    border: 'none',
                                  }}
                                  align="center"
                                >
                                  Qté
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: '#A3AED0',
                                    fontWeight: 700,
                                    border: 'none',
                                  }}
                                  align="right"
                                >
                                  Prix Unitaire
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedOrder?.details_commandes?.map(
                                (item, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell
                                      sx={{
                                        color: 'white',
                                        borderBottom:
                                          '1px solid rgba(255,255,255,0.05)',
                                      }}
                                    >
                                      {item.label_offre_detcmd}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        color: '#A3AED0',
                                        borderBottom:
                                          '1px solid rgba(255,255,255,0.05)',
                                      }}
                                    >
                                      {item.dsp_offre_detcmd}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        color: 'white',
                                        borderBottom:
                                          '1px solid rgba(255,255,255,0.05)',
                                      }}
                                      align="center"
                                    >
                                      {item.qnt_offre_detcmd}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        color: '#05CD99',
                                        fontWeight: 700,
                                        borderBottom:
                                          '1px solid rgba(255,255,255,0.05)',
                                      }}
                                      align="right"
                                    >
                                      {item.pu_offre_detcmd} €
                                    </TableCell>
                                  </TableRow>
                                )
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
                {tabValue === 1 && (
                  <>
                    <Typography variant="h6" fontWeight={700} mb={3}>
                      Liste des abonnements
                    </Typography>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={thStyle}>ID</TableCell>
                            <TableCell sx={thStyle}>Abonnement</TableCell>
                            <TableCell sx={thStyle}>Date achat</TableCell>
                            <TableCell sx={thStyle}>Renouvellement</TableCell>
                            <TableCell sx={thStyle}>Expiration</TableCell>
                            <TableCell sx={thStyle}>Prix</TableCell>
                            <TableCell sx={thStyle}>Statut</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {paginatedSubscriptions.length > 0 ? (
                            paginatedSubscriptions.map((sub) => (
                              <TableRow key={sub.id}>
                                {/* ID */}
                                <TableCell sx={tdStyle}>#{sub.id}</TableCell>

                                {/* Nom abonnement */}
                                <TableCell sx={tdStyle}>
                                  {sub.subscription?.name_subs || '—'}
                                </TableCell>

                                {/* Date achat */}
                                <TableCell sx={tdStyle}>
                                  {sub.date_buy_client_subs
                                    ? new Date(
                                        sub.date_buy_client_subs
                                      ).toLocaleDateString('fr-FR')
                                    : '—'}
                                </TableCell>

                                {/* Renouvellement */}
                                <TableCell sx={tdStyle}>
                                  {sub.date_renew_client_subs
                                    ? new Date(
                                        sub.date_renew_client_subs
                                      ).toLocaleDateString('fr-FR')
                                    : '—'}
                                </TableCell>

                                {/* Expiration */}
                                <TableCell sx={tdStyle}>
                                  {sub.date_expire_client_subs
                                    ? new Date(
                                        sub.date_expire_client_subs
                                      ).toLocaleDateString('fr-FR')
                                    : '—'}
                                </TableCell>

                                {/* Prix */}
                                <TableCell sx={tdStyle}>
                                  {sub.price_client_subs} €
                                </TableCell>

                                {/* Statut */}
                                <TableCell sx={tdStyle}>
                                  <Chip
                                    label={
                                      sub.status_client_subs === '1'
                                        ? 'Actif'
                                        : 'Inactif'
                                    }
                                    size="small"
                                    sx={{
                                      bgcolor:
                                        sub.status_client_subs === '1'
                                          ? 'rgba(5, 205, 153, 0.1)'
                                          : 'rgba(238, 93, 80, 0.1)',
                                      color:
                                        sub.status_client_subs === '1'
                                          ? '#05CD99'
                                          : '#EE5D50',
                                      fontWeight: 700,
                                      fontSize: '10px',
                                      textTransform: 'uppercase',
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={7}
                                align="center"
                                sx={{ py: 8, border: 'none' }}
                              >
                                <Typography
                                  sx={{
                                    color: 'rgba(255,255,255,0.3)',
                                    fontStyle: 'italic',
                                  }}
                                >
                                  Aucun abonnement trouvé.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Pagination */}
                    {totalSubscriptionPages > 1 && (
                      <Stack
                        spacing={2}
                        alignItems="center"
                        sx={{ mt: 3, mb: 1 }}
                      >
                        <AppPagination
                          count={totalSubscriptionPages}
                          page={subscriptionPage}
                          onChange={(e, p) => setSubscriptionPage(p)}
                          color="primary"
                          size="small"
                        />
                      </Stack>
                    )}
                  </>
                )}

                {tabValue === 2 && (
                  <>
                    <Typography variant="h6" fontWeight={700} mb={3}>
                      Liste des réservations
                    </Typography>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={thStyle}>ID</TableCell>
                            <TableCell sx={thStyle}>Offre</TableCell>
                            <TableCell sx={thStyle}>Date</TableCell>
                            <TableCell sx={thStyle}>Début</TableCell>
                            <TableCell sx={thStyle}>Fin</TableCell>
                            <TableCell sx={thStyle}>Prix</TableCell>
                            <TableCell sx={thStyle}>Statut</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {selectedClient?.reservations?.length > 0 ? (
                            paginatedReservations.map((res) => (
                              <TableRow key={res.id_offre_reservation}>
                                {/* ID */}
                                <TableCell sx={tdStyle}>
                                  #{res.id_offre_reservation}
                                </TableCell>

                                {/* Offre */}
                                <TableCell sx={tdStyle}>
                                  {res.offre?.info_offre?.titre_offre || '—'}
                                </TableCell>

                                {/* Date */}
                                <TableCell sx={tdStyle}>
                                  {new Date(
                                    res.start_time_reservation
                                  ).toLocaleDateString('fr-FR')}
                                </TableCell>

                                {/* Début */}
                                <TableCell sx={tdStyle}>
                                  {new Date(
                                    res.start_time_reservation
                                  ).toLocaleTimeString('fr-FR', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </TableCell>

                                {/* Fin */}
                                <TableCell sx={tdStyle}>
                                  {new Date(
                                    res.end_time_reservation
                                  ).toLocaleTimeString('fr-FR', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </TableCell>

                                {/* Prix */}
                                <TableCell sx={tdStyle}>
                                  {res.offre?.info_offre?.prix_offre} €
                                </TableCell>

                                {/* Statut */}
                                <TableCell sx={tdStyle}>
                                  <Chip
                                    label={res.status_reservation}
                                    size="small"
                                    sx={{
                                      bgcolor:
                                        res.status_reservation === 'approved'
                                          ? 'rgba(5, 205, 153, 0.1)'
                                          : res.status_reservation === 'pending'
                                            ? 'rgba(255, 184, 0, 0.1)'
                                            : 'rgba(68, 129, 235, 0.1)',
                                      color:
                                        res.status_reservation === 'approved'
                                          ? '#05CD99'
                                          : res.status_reservation === 'pending'
                                            ? '#FFB800'
                                            : '#4481EB',
                                      fontWeight: 700,
                                      fontSize: '10px',
                                      textTransform: 'uppercase',
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={7}
                                align="center"
                                sx={{ py: 8, border: 'none' }}
                              >
                                <Typography
                                  sx={{
                                    color: 'rgba(255,255,255,0.3)',
                                    fontStyle: 'italic',
                                  }}
                                >
                                  Aucune réservation trouvée.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {totalReservationPages > 1 && (
                      <Stack
                        spacing={2}
                        alignItems="center"
                        sx={{ mt: 3, mb: 1 }}
                      >
                        <AppPagination
                          count={totalReservationPages}
                          page={reservationPage}
                          onChange={(e, p) => setReservationPage(p)}
                          color="primary"
                          size="small"
                        />
                      </Stack>
                    )}
                  </>
                )}

                {tabValue === 3 && (
                  <Box sx={{ animation: 'fadeIn 0.3s ease-in-out' }}>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      mb={3}
                      sx={{ color: 'white' }}
                    >
                      Paramètres du client
                    </Typography>

                    {/* 1. BLOC IDENTITÉ */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {[
                        { label: 'Nom', key: 'nom_client' },
                        { label: 'Prénom', key: 'prenom_client' },
                        { label: 'Email', key: 'email_client' },
                      ].map((field) => (
                        <Grid item xs={12} sm={6} key={field.key}>
                          <TextField {...getFieldProps(field)} />
                        </Grid>
                      ))}
                    </Grid>

                    {/* 2. BLOC CONTACT */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {[
                        { label: 'Téléphone', key: 'phone_client' },
                        { label: 'Adresse', key: 'addresse_client' },
                        { label: 'Ville', key: 'ville_client' },
                      ].map((field) => (
                        <Grid item xs={12} sm={6} key={field.key}>
                          <TextField {...getFieldProps(field)} />
                        </Grid>
                      ))}
                    </Grid>

                    {/* 3. BLOC ADRESSE */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {[
                        { label: 'Code Postal', key: 'cp_client' },
                        { label: 'Province', key: 'province_client' },
                      ].map((field) => (
                        <Grid item xs={12} sm={6} key={field.key}>
                          <TextField {...getFieldProps(field)} />
                        </Grid>
                      ))}
                    </Grid>

                    {/* Boutons d'action */}
                    <Box
                      sx={{
                        mt: 5,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 2,
                      }}
                    >
                      <Button
                        variant="text"
                        sx={{
                          color: '#EE5D50',
                          fontWeight: 600,
                          textTransform: 'none',
                        }}
                        onClick={() => setEditClient(selectedClient)}
                      >
                        Annuler
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleUpdateClient}
                        sx={{
                          bgcolor: '#4318FF',
                          borderRadius: '12px',
                          px: 4,
                          fontWeight: 700,
                          textTransform: 'none',
                          '&:hover': { bgcolor: '#3211d1' },
                        }}
                      >
                        Sauvegarder les modifications
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      {/* --- DIALOG DE CRÉATION --- */}
      <Drawer
        anchor="right"
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        sx={{ zIndex: 3000 }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 500 },
            p: 3,
            bgcolor: '#FFFFFF', // Fond du panneau en blanc
            borderLeft: '1px solid #E5E7EB',
          },
        }}
      >
        {/* --- HEADER --- */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827' }}>
            {' '}
            {/* Titre en noir */}
            Nouvelle Inscription Client
          </Typography>
          <IconButton onClick={() => setOpenCreateDialog(false)}>
            <CloseIcon sx={{ color: '#111827' }} />
          </IconButton>
        </Box>

        {/* --- CORPS --- */}
        <Box
          sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 100px)', pr: 1 }}
        >
          <Stack spacing={2.5}>
            {/* Section Photo */}
            <Box sx={{ textAlign: 'center', mb: 1 }}>
              <Avatar
                src={addPreview}
                sx={{
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  mb: 1.5,
                  border: '2px dashed #D1D5DB',
                  bgcolor: '#F9FAFB', // Gris très clair pour l'avatar vide
                }}
              />
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="client-photo-upload"
                type="file"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setAddFile(file);
                    setAddPreview(URL.createObjectURL(file));
                  }
                }}
              />
              <label htmlFor="client-photo-upload">
                <Button
                  variant="text"
                  component="span"
                  size="small"
                  sx={{ textTransform: 'none', color: '#6B7280' }} // Texte bouton gris
                  startIcon={<PhotoCamera />}
                >
                  Ajouter un logo
                </Button>
              </label>
            </Box>

            {/* --- CHAMPS DE SAISIE (COLORÉS ICI) --- */}
            {/* IMPORTANT: On applique un style global aux TextField via sx 
               pour forcer le fond blanc et les bordures grises 
            */}
            <Stack
              spacing={2.5}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#FFFFFF', // Force le fond en blanc
                  color: '#111827', // Force le texte écrit en noir/bleu foncé
                  borderRadius: '12px',
                  '& fieldset': { borderColor: '#E5E7EB' }, // Bordure grise
                  '&:hover fieldset': { borderColor: '#D1D5DB' },
                },
                '& .MuiInputLabel-root': { color: '#6B7280' }, // Label en gris
              }}
            >
              <Stack direction="row" spacing={2}>
                <TextField
                  {...getFieldPropsForCreate('prenom_client', 'Prénom')}
                  fullWidth
                  size="small"
                />
                <TextField
                  {...getFieldPropsForCreate('nom_client', 'Nom')}
                  fullWidth
                  size="small"
                />
              </Stack>

              <TextField
                {...getFieldPropsForCreate(
                  'email_client',
                  'Email professionnel'
                )}
                fullWidth
                size="small"
              />
              <TextField
                {...getFieldPropsForCreate(
                  'password_client',
                  'Mot de passe temporaire',
                  'password'
                )}
                fullWidth
                size="small"
              />

              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: '#9CA3AF',
                  textTransform: 'uppercase',
                  mt: 1,
                }}
              >
                Localisation & Contact
              </Typography>

              <TextField
                {...getFieldPropsForCreate(
                  'addresse_client',
                  'Adresse complète'
                )}
                fullWidth
                size="small"
              />

              <Stack direction="row" spacing={2}>
                <TextField
                  {...getFieldPropsForCreate('ville_client', 'Ville')}
                  fullWidth
                  size="small"
                />
                <TextField
                  {...getFieldPropsForCreate('cp_client', 'Code Postal')}
                  fullWidth
                  size="small"
                />
              </Stack>

              <Stack direction="row" spacing={2}>
                <TextField
                  {...getFieldPropsForCreate(
                    'province_client',
                    'Province / État'
                  )}
                  fullWidth
                  size="small"
                />
                <TextField
                  {...getFieldPropsForCreate('phone_client', 'Téléphone')}
                  fullWidth
                  size="small"
                />
              </Stack>
            </Stack>

            {/* --- BOUTON FINAL --- */}
            <Button
              fullWidth
              variant="contained"
              onClick={handleCreateClient}
              sx={{
                py: 1.5,
                borderRadius: '10px',
                bgcolor: '#4318FF', // Bouton Noir
                color: '#FFFFFF',
                fontWeight: 700,
                mt: 3,
                '&:hover': { bgcolor: 'rgb(44, 74, 116)' },
              }}
            >
              Finaliser l'inscription
            </Button>

            <Button
              fullWidth
              onClick={() => setOpenCreateDialog(false)}
              sx={{
                py: 1.5,
                borderRadius: '10px',
                bgcolor: '#4318FF', // Bouton Noir
                color: '#FFFFFF',
                fontWeight: 700,
                mt: 3,
                '&:hover': { bgcolor: '#233e63' },
              }}
            >
              Annuler
            </Button>
          </Stack>
        </Box>
      </Drawer>
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Supprimer le compte ?"
        message={`Voulez-vous vraiment supprimer définitivement le compte de ${clientToDelete?.prenom_client} ?`}
      />
    </Box>
  );
};

export default Clients;
